import { useEffect, useState } from "react";
import GetDeplacement from "../../../Services/Admin/GetDeplacement";
import DeplacementService from "../../../Services/Magasin/DeplacementService";
import { NewSideBarAdmin } from "../../NewSideBarAdmin";

// ─── Types ────────────────────────────────────────────────────────────────────

interface DeplaceHistoryRecord {
  id?: number;
  IdCommande?: string | number;
  MagasinName: string;
  MagasinIntermediaire: string;
  Commision: any;
  Status: string;
  Client: string;
  Adresse: string;
  Date: any;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const parseDateParts = (dateInput: any): { dateStr: string; timeStr: string } => {
  if (!dateInput) return { dateStr: "--/--/----", timeStr: "--:--" };

  if (typeof dateInput === "string") {
    const s = dateInput.trim();
    const match = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (match) {
      const [, year, month, day, hour, minute] = match;
      const dateObj = new Date(Number(year), Number(month) - 1, Number(day));
      const dateStr = !isNaN(dateObj.getTime())
        ? dateObj.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })
        : `${day}/${month}/${year}`;
      const timeStr = `${hour}:${minute}`;
      return { dateStr, timeStr };
    }
  }

  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return { dateStr: "--/--/----", timeStr: "--:--" };

  return {
    dateStr: d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }),
    timeStr: d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
  };
};

const formatDate = (iso: any) => parseDateParts(iso).dateStr;
const formatTime = (iso: any) => parseDateParts(iso).timeStr;

// ─── Status Config ────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  en_attente: { label: "En attente", color: "#92400e", bg: "#fef3c7", dot: "#f59e0b" },
  confirmé:   { label: "Confirmé",   color: "#065f46", bg: "#d1fae5", dot: "#10b981" },
  confirme:   { label: "Confirmé",   color: "#065f46", bg: "#d1fae5", dot: "#10b981" },
  annulé:     { label: "Annulé",     color: "#991b1b", bg: "#fee2e2", dot: "#ef4444" },
  annule:     { label: "Annulé",     color: "#991b1b", bg: "#fee2e2", dot: "#ef4444" },
};

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconStore = () => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 7l8-5 8 5v12H2V7z" /><rect x="6" y="13" width="4" height="6" rx="0.5" /><rect x="11" y="11" width="4" height="4" rx="0.5" />
  </svg>
);
const IconArrow = () => (
  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 10h12M10 4l6 6-6 6" />
  </svg>
);
const IconUser = () => (
  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="7" r="3.5" /><path d="M3 18c0-3.87 3.13-7 7-7s7 3.13 7 7" />
  </svg>
);
const IconPin = () => (
  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2C7.24 2 5 4.24 5 7c0 4.25 5 11 5 11s5-6.75 5-11c0-2.76-2.24-5-5-5z" /><circle cx="10" cy="7" r="2" />
  </svg>
);
const IconHash = () => (
  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 7h12M4 13h12M8 3l-2 14M14 3l-2 14" />
  </svg>
);
const IconCoin = () => (
  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="8" /><path d="M10 6v2m0 4v2M8 8h3a1 1 0 010 2H9a1 1 0 000 2h3" />
  </svg>
);
const IconCalendar = () => (
  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="16" height="14" rx="2.5" /><path d="M2 9h16M6 2v4M14 2v4" />
  </svg>
);
const IconTrash = () => (
  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h14M8 6V4h4v2M5 6l1 11h8l1-11" />
  </svg>
);
const IconCheck = () => (
  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 10l5 5 8-8" />
  </svg>
);
const IconSearch = () => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="9" r="6" /><path d="M14 14l4 4" />
  </svg>
);
const IconWarning = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const IconCheckCircle = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><path d="M8 12l3 3 5-5" />
  </svg>
);

// ─── Dialog type ──────────────────────────────────────────────────────────────
type DialogAction = "supprimer" | "confirmer";

interface ActionDialogProps {
  open: boolean;
  action: DialogAction | null;
  record: DeplaceHistoryRecord | null;
  onConfirm: () => void;
  onClose: () => void;
  loading?: boolean;
}

// ─── Action Confirm Dialog ────────────────────────────────────────────────────
const ActionDialog = ({ open, action, record, onConfirm, onClose, loading: isLoading }: ActionDialogProps) => {
  if (!open || !record || !action) return null;

  const isDelete = action === "supprimer";

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1.5rem",
        animation: "deplace-a-fadeIn 0.18s ease",
      }}
    >
      <div style={{
        background: "#fff", borderRadius: "22px",
        padding: "2rem", maxWidth: "400px", width: "100%",
        boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
        animation: "deplace-a-scaleIn 0.24s cubic-bezier(0.34,1.56,0.64,1)",
      }}>
        {/* Icon */}
        <div style={{
          width: "56px", height: "56px", borderRadius: "16px",
          background: isDelete ? "#fee2e2" : "#d1fae5",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: "1.25rem",
        }}>
          {isDelete ? <IconWarning /> : <IconCheckCircle />}
        </div>

        {/* Title */}
        <p style={{
          fontFamily: "'Syne', sans-serif", fontWeight: 900,
          fontSize: "1.15rem", margin: "0 0 8px", color: "#111",
        }}>
          {isDelete ? "Supprimer l'enregistrement ?" : "Confirmer le dépôt ?"}
        </p>

        {/* Body */}
        <p style={{ fontSize: "0.8375rem", color: "#6b7280", lineHeight: 1.65, margin: "0 0 4px" }}>
          Commande{" "}
          <strong style={{ color: "#FFB800" }}>#{record.id}</strong>
          {" "}— {record.Client}
        </p>
        <p style={{ fontSize: "0.8125rem", color: "#9ca3af", margin: "0 0 1.5rem" }}>
          {isDelete
            ? "Cette action est irréversible."
            : `La commande sera marquée comme confirmée chez ${record.MagasinIntermediaire}.`}
        </p>

        {/* Buttons */}
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: "0.8rem", borderRadius: "12px",
              border: "1px solid #e5e7eb", background: "#fff",
              fontSize: "0.875rem", fontWeight: 600,
              cursor: "pointer", color: "#374151",
              transition: "background 0.15s",
            }}
          >
            Retour
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            style={{
              flex: 2, padding: "0.8rem", borderRadius: "12px", border: "none",
              background: isDelete ? "#ef4444" : "#10b981",
              color: "#fff", fontSize: "0.875rem", fontWeight: 700,
              cursor: isLoading ? "not-allowed" : "pointer", opacity: isLoading ? 0.7 : 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: "7px",
              transition: "opacity 0.15s",
            }}
          >
            {isLoading
              ? <><div style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "deplace-a-spin 0.65s linear infinite" }} /> Traitement…</>
              : isDelete ? <><IconTrash /> Supprimer</> : <><IconCheck /> Oui, confirmer</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── History Card ─────────────────────────────────────────────────────────────
interface HistoryCardProps {
  record: DeplaceHistoryRecord;
  index: number;
  onAction: (record: DeplaceHistoryRecord, action: DialogAction) => void;
}

const HistoryCard = ({ record, index, onAction }: HistoryCardProps) => {
  const sc = STATUS_CONFIG[record.Status] ?? STATUS_CONFIG["en_attente"];
  const isPending = record.Status === "en_attente";

  return (
    <div
      className="-deplace-a-info-card"
      style={{
        background: "#fff",
        border: "1px solid rgba(0,0,0,0.07)",
        borderRadius: "18px",
        overflow: "hidden",
        boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
        animationDelay: `${index * 0.055}s`,
        transition: "transform 0.2s, box-shadow 0.2s",
      }}
    >
      {/* ── Card Header ── */}
      <div style={{
        background: "linear-gradient(135deg,#fffdf5 0%,#fff8e6 100%)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        padding: "13px 18px",
        display: "flex", alignItems: "center",
        justifyContent: "space-between", flexWrap: "wrap", gap: "10px",
      }}>
        {/* Magasin flow */}
        <div className="-deplace-a-info-flow" style={{ display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div style={{
              width: "30px", height: "30px", borderRadius: "8px",
              background: "#fef3c7",
              display: "flex", alignItems: "center", justifyContent: "center", color: "#d97706",
            }}>
              <IconStore />
            </div>
            <span style={{ fontSize: "0.8125rem", fontWeight: 700, color: "#111" }}>{record.MagasinName}</span>
          </div>
          <span style={{ color: "#fbbf24" }}><IconArrow /></span>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div style={{
              width: "30px", height: "30px", borderRadius: "8px",
              background: "#fff7ed",
              display: "flex", alignItems: "center", justifyContent: "center", color: "#ea580c",
            }}>
              <IconStore />
            </div>
            <span style={{ fontSize: "0.8125rem", fontWeight: 700, color: "#111" }}>{record.MagasinIntermediaire}</span>
          </div>
        </div>

        {/* Date + Status */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            padding: "4px 10px", borderRadius: "20px",
            background: "#f3f4f6", color: "#6b7280",
            fontSize: "0.72rem", fontWeight: 600,
          }}>
            <IconCalendar />
            <span>{formatDate(record.Date)}</span>
            <span style={{ color: "#d1d5db" }}>·</span>
            <span>{formatTime(record.Date)}</span>
          </div>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            padding: "4px 12px", borderRadius: "20px",
            background: sc.bg, color: sc.color,
            fontSize: "0.72rem", fontWeight: 700,
          }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: sc.dot, flexShrink: 0 }} />
            {sc.label}
          </div>
        </div>
      </div>

      {/* ── Card Body ── */}
      <div style={{ padding: "16px 18px" }}>
        <div
          className="-deplace-a-info-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "13px",
            marginBottom: "16px",
          }}
        >
          {/* Client */}
          <div className="-deplace-a-info-field">
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px", color: "#9ca3af" }}>
              <IconUser />
              <span style={{ fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Client</span>
            </div>
            <p style={{ margin: 0, fontSize: "0.875rem", fontWeight: 600, color: "#111" }}>{record.Client}</p>
          </div>

          {/* Adresse */}
          <div className="-deplace-a-info-field">
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px", color: "#9ca3af" }}>
              <IconPin />
              <span style={{ fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Adresse</span>
            </div>
            <p style={{ margin: 0, fontSize: "0.875rem", fontWeight: 600, color: "#111" }}>{record.Adresse}</p>
          </div>

          {/* N° Commande */}
          <div className="-deplace-a-info-field">
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px", color: "#9ca3af" }}>
              <IconHash />
              <span style={{ fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>N° Commande</span>
            </div>
            <p style={{ margin: 0, fontSize: "0.875rem", fontWeight: 700, color: "#c78f02", fontFamily: "'DM Mono', monospace" }}>
              #{record.IdCommande || record.id}
            </p>
          </div>

          {/* Commission */}
          <div className="-deplace-a-info-field">
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px", color: "#9ca3af" }}>
              <IconCoin />
              <span style={{ fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Commission</span>
            </div>
            <span style={{
              background: "linear-gradient(135deg,#fffbeb,#fef3c7)",
              color: "#92400e",
              padding: "3px 10px", borderRadius: "8px",
              fontSize: "0.875rem", fontWeight: 800,
              display: "inline-block",
              border: "1px solid rgba(245,158,11,0.2)",
            }}>
              {Number(record.Commision).toFixed(2)} MAD
            </span>
          </div>
        </div>

        {/* ── Footer: timestamp + action buttons ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderTop: "1px solid rgba(0,0,0,0.05)", paddingTop: "14px",
          flexWrap: "wrap", gap: "12px",
        }}>
          {/* Timestamp */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#9ca3af" }}>
            <IconCalendar />
            <span style={{ fontSize: "0.79rem", fontWeight: 500 }}>
              Enregistré le {formatDate(record.Date)} à {formatTime(record.Date)}
            </span>
          </div>

          {/* ── TWO ACTION BUTTONS ── */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>

            {/* Confirmer button — only shown when pending */}
            {isPending && (
              <button
                onClick={() => onAction(record, "confirmer")}
                className="-deplace-a-info-btn-confirm"
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "8px 16px", borderRadius: "10px",
                  border: "1.5px solid #6ee7b7",
                  background: "#f0fdf4", color: "#059669",
                  fontSize: "0.8125rem", fontWeight: 700,
                  cursor: "pointer", transition: "all 0.18s",
                }}
              >
                <IconCheck />
                Confirmer
              </button>
            )}

            {/* Supprimer button */}
            <button
              onClick={() => onAction(record, "supprimer")}
              className="-deplace-a-info-btn-delete"
              style={{
                display: "flex", alignItems: "center", gap: "6px",
                padding: "8px 16px", borderRadius: "10px",
                border: "1.5px solid #fca5a5",
                background: "#fff5f5", color: "#dc2626",
                fontSize: "0.8125rem", fontWeight: 700,
                cursor: "pointer", transition: "all 0.18s",
              }}
            >
              <IconTrash />
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function DisplayAllDeplacement() {
  // ✅ FIX 1: Single state variable "records" used consistently everywhere
  const [records, setRecords] = useState<DeplaceHistoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [dialog, setDialog] = useState<{
    open: boolean;
    action: DialogAction | null;
    record: DeplaceHistoryRecord | null;
  }>({ open: false, action: null, record: null });

  const [activeFilter, setActiveFilter] = useState<"all" | string>("all");
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

  useEffect(() => {
    setLoading(true);
    GetDeplacement().GetAllDeplacement()
      .then((res) => {
        setRecords(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error("Admin GetAllDeplacement error:", err);
        setRecords([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const openDialog = (record: DeplaceHistoryRecord, action: DialogAction) =>
    setDialog({ open: true, action, record });

  const closeDialog = () =>
    setDialog({ open: false, action: null, record: null });

  const handleConfirmAction = async () => {
    if (!dialog.record || !dialog.action) return;
    setActionLoading(true);
    try {
      if (dialog.action === "supprimer") {
        await DeplacementService().deleteDeplacement(dialog.record.id!);
        setRecords((prev) => prev.filter((r) => r.id !== dialog.record!.id));
      } else if (dialog.action === "confirmer") {
        await DeplacementService().confirmeDeplacement(dialog.record.id!);
        setRecords((prev) =>
          prev.map((r) =>
            r.id === dialog.record!.id
              ? { ...r, Status: "confirmé" }
              : r
          )
        );
      }
      closeDialog();
    } catch (error) {
      console.error("Erreur action deplacement:", error);
      alert("Une erreur est survenue lors de l'opération.");
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ FIX 4: filters now correctly references "records" state
  const filters: { key: "all" | string; label: string; count: number }[] = [
    { key: "all",        label: "Tous",       count: records.length },
    { key: "en_attente", label: "En attente", count: records.filter((r) => (r.Status || "").toLowerCase() === "en_attente").length },
    { key: "confirmé",   label: "Confirmés",  count: records.filter((r) => (r.Status || "").toLowerCase() === "confirmé" || (r.Status || "").toLowerCase() === "confirme").length },
    { key: "annulé",     label: "Annulés",    count: records.filter((r) => (r.Status || "").toLowerCase() === "annulé" || (r.Status || "").toLowerCase() === "annule").length },
  ];

  // ✅ FIX 5: filtered now correctly references "records" state
  const filtered = records
    .filter((r) => {
      if (activeFilter === "all") return true;
      const s = (r.Status || "").toLowerCase();
      if (activeFilter === "confirmé") return s === "confirmé" || s === "confirme";
      if (activeFilter === "annulé") return s === "annulé" || s === "annule";
      return s === activeFilter.toLowerCase();
    })
    .filter((r) =>
      search === "" ||
      (r.Client && r.Client.toLowerCase().includes(search.toLowerCase())) ||
      String(r.id ?? "").includes(search) ||
      (r.IdCommande && String(r.IdCommande).includes(search)) ||
      (r.MagasinName && r.MagasinName.toLowerCase().includes(search.toLowerCase())) ||
      (r.MagasinIntermediaire && r.MagasinIntermediaire.toLowerCase().includes(search.toLowerCase())) ||
      (r.Adresse && r.Adresse.toLowerCase().includes(search.toLowerCase()))
    )
    .sort((a, b) => {
      const timeA = new Date(String(a.Date || "").replace(" ", "T")).getTime() || 0;
      const timeB = new Date(String(b.Date || "").replace(" ", "T")).getTime() || 0;
      return sortOrder === "desc" ? timeB - timeA : timeA - timeB;
    });

  const totalCommission = records
    .filter((r) => {
      const s = (r.Status || "").toLowerCase();
      return s === "confirmé" || s === "confirme";
    })
    .reduce((sum, r) => sum + (Number(r.Commision) || 0), 0);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@500&display=swap');

        @keyframes deplace-a-fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes deplace-a-scaleIn {
          from { opacity:0; transform:scale(0.92) translateY(12px) }
          to   { opacity:1; transform:scale(1)    translateY(0)    }
        }
        @keyframes deplace-a-spin { to { transform: rotate(360deg) } }
        @keyframes deplace-a-slideUp {
          from { opacity:0; transform:translateY(16px) }
          to   { opacity:1; transform:translateY(0)    }
        }

        .-deplace-a-info-card { animation: deplace-a-slideUp 0.38s ease both; }
        .-deplace-a-info-card:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 8px 28px rgba(0,0,0,0.09) !important;
        }

        .-deplace-a-info-btn-confirm:hover {
          background: #d1fae5 !important;
          border-color: #34d399 !important;
          transform: translateY(-1px);
        }
        .-deplace-a-info-btn-delete:hover {
          background: #fee2e2 !important;
          border-color: #f87171 !important;
          transform: translateY(-1px);
        }

        .-deplace-a-info-filter-btn { transition: all 0.18s; }
        .-deplace-a-info-filter-btn:hover { border-color: #FFB800 !important; }

        .-deplace-a-info-sort-btn:hover { background: #fffbeb !important; }

        .-deplace-a-info-search-input:focus {
          outline: none;
          border-color: #FFB800 !important;
          box-shadow: 0 0 0 3px rgba(255,184,0,0.15) !important;
        }

        @media (max-width: 600px) {
          .-deplace-a-info-flow    { font-size: 0.72rem !important; }
          .-deplace-a-info-grid    { grid-template-columns: 1fr 1fr !important; }
          .-deplace-a-info-stats   { grid-template-columns: 1fr 1fr !important; }
          .-deplace-a-info-toolbar { flex-direction: column !important; }
          .-deplace-a-info-header-top { flex-direction: column !important; align-items: flex-start !important; }
          .-deplace-a-info-filters { flex-wrap: wrap !important; }
        }
      `}</style>

      <NewSideBarAdmin/>

      <div
        className="-deplace-a-info-root"
        style={{ minHeight: "100vh", background: "#f7f8fc", fontFamily: "'DM Sans', sans-serif" }}
      >
        <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "24px 20px" }}>

          {loading && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "5rem 2rem", gap: "16px" }}>
              <div style={{ width: "40px", height: "40px", border: "3.5px solid #f3f4f6", borderTopColor: "#FFB800", borderRadius: "50%", animation: "deplace-a-spin 0.7s linear infinite" }} />
              <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "#9ca3af" }}>Chargement des déplacements…</span>
            </div>
          )}

          {!loading && (
            <>
              {/* ── Toolbar ── */}
              <div
                className="-deplace-a-info-toolbar"
                style={{ display: "flex", gap: "10px", marginBottom: "14px", alignItems: "center" }}
              >
                {/* Search */}
                <div style={{ flex: 1, position: "relative", maxWidth: "380px" }}>
                  <div style={{
                    position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)",
                    color: "#9ca3af", pointerEvents: "none",
                  }}>
                    <IconSearch />
                  </div>
                  <input
                    type="text"
                    placeholder="Rechercher client, commande, magasin..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="-deplace-a-info-search-input"
                    style={{
                      width: "100%", padding: "9px 12px 9px 38px",
                      borderRadius: "12px",
                      border: "1px solid rgba(0,0,0,0.1)",
                      fontSize: "0.8375rem", color: "#111",
                      background: "#fff", outline: "none",
                      transition: "border-color 0.15s, box-shadow 0.15s",
                    }}
                  />
                </div>

                {/* Sort */}
                <button
                  className="-deplace-a-info-sort-btn"
                  onClick={() => setSortOrder((s) => (s === "desc" ? "asc" : "desc"))}
                  style={{
                    display: "flex", alignItems: "center", gap: "6px",
                    padding: "9px 14px", borderRadius: "12px",
                    border: "1px solid rgba(0,0,0,0.1)",
                    background: "#fff", color: "#374151",
                    fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    {sortOrder === "desc"
                      ? <path d="M6 3v14M6 17l-3-3M6 17l3-3M14 17V3M14 3l-3 3M14 3l3 3" />
                      : <path d="M6 3v14M6 3l-3 3M6 3l3 3M14 17V3M14 17l-3-3M14 17l3-3" />}
                  </svg>
                  {sortOrder === "desc" ? "Plus récent" : "Plus ancien"}
                </button>
              </div>

              {/* ── Filters ── */}
              <div
                className="-deplace-a-info-filters"
                style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}
              >
                {filters.map((f) => (
                  <button
                    key={f.key}
                    className="-deplace-a-info-filter-btn"
                    onClick={() => setActiveFilter(f.key)}
                    style={{
                      padding: "7px 15px", borderRadius: "20px",
                      border: activeFilter === f.key ? "2px solid #FFB800" : "1px solid rgba(0,0,0,0.1)",
                      background: activeFilter === f.key ? "#FFB800" : "#fff",
                      color: activeFilter === f.key ? "#fff" : "#374151",
                      fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer",
                      display: "flex", alignItems: "center", gap: "6px",
                    }}
                  >
                    {f.label}
                    <span style={{
                      background: activeFilter === f.key ? "rgba(255,255,255,0.28)" : "#f3f4f6",
                      color: activeFilter === f.key ? "#fff" : "#6b7280",
                      borderRadius: "10px", padding: "1px 7px",
                      fontSize: "0.7rem", fontWeight: 700,
                    }}>
                      {f.count}
                    </span>
                  </button>
                ))}
                {filtered.length !== records.length && (
                  <span style={{ marginLeft: "auto", fontSize: "0.8rem", color: "#9ca3af", alignSelf: "center", fontWeight: 500 }}>
                    {filtered.length} résultat{filtered.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {/* ── Cards ── */}
              {filtered.length === 0 ? (
                <div style={{
                  textAlign: "center", padding: "3.5rem",
                  background: "#fff", borderRadius: "18px",
                  border: "1.5px dashed rgba(0,0,0,0.1)",
                }}>
                  <div style={{
                    width: "48px", height: "48px", borderRadius: "14px",
                    background: "#fff8e6", display: "flex", alignItems: "center",
                    justifyContent: "center", margin: "0 auto 12px",
                  }}>
                    <IconSearch />
                  </div>
                  <p style={{ color: "#374151", fontWeight: 700, margin: "0 0 4px", fontSize: "0.9375rem" }}>
                    Aucun enregistrement trouvé
                  </p>
                  {search && (
                    <p style={{ color: "#9ca3af", fontSize: "0.8rem", margin: 0 }}>
                      Essayez un autre terme de recherche
                    </p>
                  )}
                </div>
              ) : (
                <div className="-deplace-a-info-list" style={{ display: "flex", flexDirection: "column", gap: "13px" }}>
                  {filtered.map((r, i) => (
                    <HistoryCard key={r.id ?? i} record={r} index={i} onAction={openDialog} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Dialog ── */}
      <ActionDialog
        open={dialog.open}
        action={dialog.action}
        record={dialog.record}
        onConfirm={handleConfirmAction}
        onClose={closeDialog}
        loading={actionLoading}
      />
    </>
  );
}